import { Controller } from "@hotwired/stimulus"

// Initializes jQuery UI plugins (select2, selectize, multiselect, summernote,
// pickadate) on elements within this controller's scope. Also reinitializes
// select2 on dynamically inserted cocoon nested-form fields.
//
// Tears down before Turbo caches the page to prevent duplicate widgets
// when restoring from cache.
export default class extends Controller {
  connect() {
    this.initEnhancements(this.element)
    // Cocoon emits via jQuery's .trigger(), which doesn't reach
    // addEventListener for colon-namespaced events — bind with jQuery.
    this.cocoonHandler = (_e, insertedItem) => {
      const node = insertedItem instanceof jQuery ? insertedItem[0] : insertedItem
      if (node) this.initEnhancements(node)
    }
    jQuery(this.element).on('cocoon:after-insert', this.cocoonHandler)

    this.beforeCacheHandler = () => this.teardown()
    document.addEventListener('turbo:before-cache', this.beforeCacheHandler)

    this.renderHandler = () => this.initEnhancements(this.element)
    document.addEventListener('turbo:render', this.renderHandler)

    this.frameLoadHandler = (e) => this.initEnhancements(e.target)
    document.addEventListener('turbo:frame-load', this.frameLoadHandler)

    // turbo_overlay morphs an overlay's frame on form re-render rather than
    // replacing it. Tear down select2 widgets first so the morph diffs against
    // the original <select> + sibling .help-block, not the wrapped widget
    // span — otherwise the morph drops the help-block error message.
    this.beforeFrameRenderHandler = (e) => this.teardownEnhancements(e.target)
    document.addEventListener('turbo:before-frame-render', this.beforeFrameRenderHandler)
  }

  disconnect() {
    jQuery(this.element).off('cocoon:after-insert', this.cocoonHandler)
    document.removeEventListener('turbo:before-cache', this.beforeCacheHandler)
    document.removeEventListener('turbo:render', this.renderHandler)
    document.removeEventListener('turbo:frame-load', this.frameLoadHandler)
    document.removeEventListener('turbo:before-frame-render', this.beforeFrameRenderHandler)
    this.teardown()
  }

  initEnhancements(container) {
    const $el = jQuery(container)

    // Match select elements only — select2 adds the `select2` class to its own
    // widget span, so a bare `.select2` selector also matches the widget on
    // re-runs and silently destroys the next sibling (e.g. the .help-block
    // simple_form renders for validation errors).
    // Inside a <dialog>, attach the dropdown to the dialog so it renders in
    // the top layer; otherwise it'd be hidden behind the backdrop.
    const select2Options = (el) => {
      const dialog = el.closest('dialog')
      const opts = { width: '100%', theme: 'bootstrap' }
      if (dialog) opts.dropdownParent = jQuery(dialog)
      // If the select carries data-ajax-url, wire up an ajax data source so the
      // dropdown queries the server as the user types instead of expecting
      // every possible option to be pre-rendered.
      if (el.dataset.ajaxUrl) {
        opts.ajax = {
          url: el.dataset.ajaxUrl,
          dataType: 'json',
          delay: 250,
          data: (params) => ({ q: params.term }),
          processResults: (data) => data,
          cache: true
        }
      }
      return opts
    }
    $el.find('select.select2:not(.select2-hidden-accessible)').each(function() {
      jQuery(this).select2(select2Options(this))
    })
    $el.find('select.select2multi:not(.select2-hidden-accessible)').each(function() {
      jQuery(this).select2(select2Options(this))
    })
    $el.find('[data-enhancement=selectize]:not(.selectized)').each(function() {
      // Reset browser-autofilled value to the server-rendered selected option.
      // Prevents browser refresh from picking a random option.
      const selected = this.querySelector('option[selected]')
      this.value = selected ? selected.value : (this.querySelector('option')?.value || '')
    }).selectize()
    $el.find('[data-enhancement=multiselect]:not(.multiselect-initialized)').each(function() {
      jQuery(this).multiselect({ enableFiltering: true })
      jQuery(this).addClass('multiselect-initialized')
    })

    $el.find('[data-enhancement=wysiwyg]:not(.summernote-initialized)').each(function() {
      jQuery(this).summernote({
        height: 300,
        toolbar: [
          ['style', ['bold', 'italic', 'underline', 'clear']],
          ['font', ['strikethrough', 'superscript', 'subscript']],
          ['para', ['ul', 'ol', 'paragraph']],
          ['insert', ['link', 'table', 'hr']],
          ['view', ['fullscreen', 'codeview']]
        ]
      })
      jQuery(this).addClass('summernote-initialized')
    })

    $el.find('[data-enhancement=pickadate]:not(.picker__input)').pickadate({
      min: [1990, 0, 1],
      max: new Date(),
      selectYears: 20,
      selectMonths: true,
      format: 'mm/dd/yyyy',
      onClose: function() { jQuery(document.activeElement).blur() }
    })

    $el.find('[data-enhancement=pickadate_no_limit]:not(.picker__input)').pickadate({
      selectYears: 20,
      selectMonths: true,
      format: 'mm/dd/yyyy',
      onClose: function() { jQuery(document.activeElement).blur() }
    })
  }

  teardown() {
    this.teardownEnhancements(this.element)
  }

  teardownEnhancements(container) {
    const $el = jQuery(container)
    $el.find('select.select2, select.select2multi').each(function() {
      if (jQuery(this).data('select2')) jQuery(this).select2('destroy')
    })
    $el.find('[data-enhancement=wysiwyg]').each(function() {
      if (jQuery(this).data('summernote')) {
        jQuery(this).summernote('destroy')
        jQuery(this).removeClass('summernote-initialized')
      }
    })
    $el.find('[data-enhancement=selectize]').each(function() {
      if (this.selectize) this.selectize.destroy()
    })
    $el.find('[data-enhancement=multiselect]').each(function() {
      jQuery(this).removeClass('multiselect-initialized')
    })
  }
}
